import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { URLSearchParams } from '@angular/http';
import * as moment from 'moment/moment';

import { DataMinerQueryEncoder } from '../../../shared/dataminer-query-encoder';
import { ConfigurationService } from '../../../shared/configuration.service';
import { FeedService } from './feed.service';
import { FeedMetadataService } from './feed-metadata.service';
import { FeedColumnLookupService } from './feed-column-lookup.service';
import { HttpUtilityService } from '../../../shared/http-utility.service';
import { FeedSelectorConfigurationService} from './feed-selector-configuration.service';

import { FeedSearchResult } from './models/feed-search-result.model';
import { SearchSpecification } from './models/search-specification.model';
import { Configuration } from '../../../shared/configuration.model';
import { FeedMetadataColumn } from './models/feed-metadata-column.model';
import { FeedMetadataDetail } from './models/feed-metadata-detail.model';
import { FeedColumnLookup } from './models/feed-column-lookup.model';
import { SearchPagingResult } from './models/search-paging-result.model';
import { IColumnFormat } from './models/column-format.model';
import { ColumnFormatString } from './models/column-format-string.model';
import { ColumnFormatBoolean } from './models/column-format-boolean.model';
import { ColumnFormatDate } from './models/column-format-date.model';
import { ColumnFormatNumber } from './models/column-format-number.model';
import { ColumnFormatCurrency } from './models/column-format-currency.model';
import { FeedSelectorConfiguration } from './models/feed-selector-configuration.model';
import { TimeDurationUnit } from '../../../shared/timepicker/time-picker-interval';

@Injectable()
export class FeedManager {

    constructor(private feedService: FeedService,
        private feedMetadataService: FeedMetadataService,
        private feedColumnLookupService: FeedColumnLookupService,
        private httpUtilityService: HttpUtilityService,
        private configurationService: ConfigurationService,
        private feedSelectorConfigService: FeedSelectorConfigurationService) {

        this.configuration = configurationService.configuration;
        this.init();
    }
    private searchSubject: Subject<FeedSearchResult> = new Subject<FeedSearchResult>();
    private metadataSubject: Subject<FeedMetadataDetail> = new Subject<FeedMetadataDetail>();
    private lookupSubject: Subject<FeedColumnLookup[]> = new Subject<FeedColumnLookup[]>();
    private exportSubject: Subject<any[]> = new Subject<any[]>();
    private timeDurationUnit;
    private configuration: Configuration;
    private errorSubject = new Subject<any>();
    dateTimeAliases: any[] = [];

    SearchObservable = this.searchSubject.asObservable();
    ErrorObservable = this.errorSubject.asObservable();

    private init() {
        this.dateTimeAliases['medium'] = 'lll';              // localized i.e. Sep 4 1986 8:30 PM
        this.dateTimeAliases['short'] = 'M/D/YYYY HH:mm';    // pjm standard - not localized i.e. 9/4/1986 13:00
        this.dateTimeAliases['mediumdatetime'] = 'M/D/YYYY HH:mm:ss'; // pjm standard - not localized i.e. 9/4/1986 13:00:00
        this.dateTimeAliases['fulldate'] = 'MMMM LL';        // localized i.e. Friday, September 4 1986
        this.dateTimeAliases['longdate'] = 'LL';             // localized i.e. September 4 1986
        this.dateTimeAliases['mediumdate'] = 'll';           // localized i.e. Sep 4 1986
        this.dateTimeAliases['shortdate'] = 'M/D/YYYY';      // pjm standard - not localized i.e. 9/4/1986
        this.dateTimeAliases['mediumtime'] = 'LTS';          // localized i.e. 8:30:25 PM
        this.dateTimeAliases['shorttime'] = 'LT';            // localized i.e. 8:30 PM
    }
    search(feedName: string, searchSpecification: SearchSpecification, isArchive: boolean = false): void {
        if (isArchive) {
            searchSpecification.order = null;
            searchSpecification.sort = null;
            searchSpecification.fields = [];
        }
        const urlSearchParams = this.buildSearchParams(searchSpecification);

        this.feedService.get(feedName, urlSearchParams).subscribe(
            result => {
                this.searchSubject.next(result);
            },
            error => {
                this.errorSubject.next(error);
            }
        );
    }

    getLinkForExport(feedName: string, searchSpecification: SearchSpecification, format: string, isArchive: boolean = false): Observable<any> {
        if (searchSpecification === undefined) {
            searchSpecification = new SearchSpecification();
        }
        if (isArchive) {
            searchSpecification.order = null;
            searchSpecification.sort = null;
            searchSpecification.fields = [];
        }

        const searchParams = this.buildSearchParams(searchSpecification);

        searchParams.delete('rowCount');

        searchParams.append('format', format);
        searchParams.append('download', 'true');

        return this.feedService.export(feedName, searchParams);
    }

    buildSearchSpecificationFilters(filters: any[], metadataDetail: FeedMetadataDetail): any[] {
        const convertedFilters = [];

        if (filters !== null && filters.length > -1) {
            Object.keys(filters).map((key) => {
                if (filters[key] && filters[key] !== 'All') {
                    const column = metadataDetail.columns.find(x => x.fieldName === key);
                    let value = filters[key];
                    if (this.isStringInputColumn(column)) {
                        // add string wildcard search characters
                        value = `*${filters[key]}*`;
                    } else if (this.isInlineDateColumn(column)) {
                        // if this is a filter value for in-line date filter
                        // try to remove the exact keyword
                        // and always add it back (just to make sure there is no duplicate exact keyword being added)
                        value = value.split(/exact/i)[0]; // case insensitive 'exact'
                        value += 'exact';
                    }

                    const filter = {};
                    filter[key] = value;
                    convertedFilters.push(filter);
                }
            });
        }

        return convertedFilters;
    }

    buildFiltersFromSearchSpecification(searchSpecFilters: any[], metadataDetail: FeedMetadataDetail): any[] {
        const convertedFilters = [];
        if (searchSpecFilters !== null && searchSpecFilters.length > -1) {
            searchSpecFilters.forEach(filter => {
                Object.keys(filter).map((key) => {
                    const column = metadataDetail.columns.find(x => x.fieldName === key);
                    let value = filter[key];
                    if (value) {
                        // console.log("build filter:" + value);console.log(column);

                        // remove string wildcard search characters
                        if (this.isStringInputColumn(column)) {
                            value = value.substring(1, value.length - 1);
                        } else if (this.isInlineDateColumn(column)) {
                            // NOTE: date INPUT doenst suport ranges, only single dates exact matches.  Only GRIDTOP support ranges.
                            // we ignore GridTop columns here and allow the other code to do similar parsing for ranges

                            // if the value is correct, this will simply parse the string value as the startDate.
                            // however, if the column's FilterUILocation metadata is WRONG compared to the default search criteria
                            // the value might be the unsupported string '{date} TO {date}', in this case we need to parse it, and
                            // just use one of the dates....startDate.
                            const parts = this.parseDateFilter(value, column);
                            value = moment(parts.startDate).format(this.getDateFormat(column).format);
                        }

                        convertedFilters[key] = value;
                    }
                });
            });
        }

        return convertedFilters;
    }

    private isStringInputColumn(column: FeedMetadataColumn): boolean {
        return column && column.dataType.toLowerCase() === 'string' && column.filterType && column.filterType.toLowerCase() === 'input';
    }

    private isInlineDateColumn(column: FeedMetadataColumn): boolean {
        // a null filterUiLocation => Input
        return column && column.filterType && column.filterType.toLowerCase() === 'date' && (!column.filterUiLocation || column.filterUiLocation.toLowerCase() === 'inline');
    }

    getFeedMetadataDetail(feedName: string): Observable<FeedMetadataDetail> {
        this.feedMetadataService.get(feedName).subscribe(
            (detail: FeedMetadataDetail) => {
                this.timeDurationUnit = detail.timeDurationUnit;

                // Need the feed key, identityField and displayField properties at the detail level
                detail.feedSelectorConfiguration = this.feedSelectorConfigService.configuration
                    .find(x => x.feedKey.toLowerCase() === feedName.toLowerCase());

                detail.columns.forEach((column: FeedMetadataColumn) => {
                    column.columnFormat = this.getColumnFormat(column);
                    column.width = column.width;

                    // get selector config if it exists
                    column.feedSelectorConfiguration = this.getColumnConfiguration(detail.columns, column.filterType);

                });

                this.metadataSubject.next(detail);
            },
            error => {
                this.errorSubject.next(error);
            }
        );

        return this.metadataSubject.asObservable();
    }

    getFeedLookupData(feedName: string, detail: FeedMetadataDetail): Observable<FeedColumnLookup[]> {
        const lookups: Observable<FeedColumnLookup>[] = [];

        detail.columns.forEach(column => {
            if (column.enableFiltering && typeof (column.filterType) === 'string' && column.filterType.toLowerCase() === 'lookup') {
                lookups.push(this.feedColumnLookupService.get(feedName, column.fieldName));
            }
        });

        Observable.forkJoin(lookups).subscribe(
            (lookupData: FeedColumnLookup[]) => {
                lookupData.forEach((lookupColumn: FeedColumnLookup) => {
                    const options = [];
                    options.push('All');
                    lookupColumn.options = options.concat(lookupColumn.options);
                });
                this.lookupSubject.next(lookupData);
            },
            error => {
                this.errorSubject.next(error);
            }
        );

        return this.lookupSubject.asObservable();
    }

    // using regex with positve and negative assertions that ensure comma is being placed in the proper place
    formatNum(num) {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    // calling formatNum function within SearchPagingResult to format totalCnt and pageCnt
    getSearchPagingResult(searchResult: FeedSearchResult): SearchPagingResult {
        const pagingResult = new SearchPagingResult();
        const startRow = searchResult.searchSpecification.startRow;
        const rowCnt = searchResult.searchSpecification.rowCount;
        let totalCnt = searchResult.totalRows || 0;

        const currentPage = totalCnt === 0 ? 1 : Math.ceil(startRow / rowCnt);
        let pageCnt = totalCnt === 0 ? 1 : Math.ceil(totalCnt / rowCnt);

        pagingResult.startRow = totalCnt === 0 ? 0 : startRow;
        pagingResult.rowCnt = rowCnt;
        pagingResult.totalCnt = totalCnt;
        pagingResult.pageCnt = pageCnt;

        pagingResult.currentPage = currentPage;
        pagingResult.previousPageStartRow = startRow - rowCnt;
        pagingResult.nextPageStartRow = startRow + rowCnt;
        pagingResult.lastPageStartRow = (pageCnt - 1) * rowCnt + 1;
        pagingResult.hasNextPage = pageCnt > currentPage;
        pagingResult.hasPrevPage = startRow > 1;

        const throughCnt = startRow === pagingResult.lastPageStartRow ? totalCnt : startRow + rowCnt - 1;
        totalCnt = this.formatNum(totalCnt);
        pageCnt = this.formatNum(pageCnt);

        pagingResult.label = `Displaying: ${pagingResult.startRow} - ${throughCnt} of ${totalCnt}, Page: ${currentPage} of ${pageCnt}`;

        return pagingResult;

    }

    getColumnFormat(column: FeedMetadataColumn): IColumnFormat {

        switch (column.dataType.toLowerCase()) {
            case 'number':
                return this.getNumberFormat(column);

            case 'date':
            case 'datetime':
                return this.getDateFormat(column);

            case 'bool':
            case 'boolean':
                return this.getBoolFormat(column);

            default:
                // default
                return new ColumnFormatString();
        }

    }

    parseDateFilter(value: string, column: FeedMetadataColumn): any {
        // supported combinations:
        //      with/without time
        //      Date range seperated by "to" keyword
        //      single date with "exact" keyword
        let startDate: Date;
        let startTimeString = '';
        let endDate: Date;
        let endTimeString = '';
        const parts = value.split(/to/i); // case insensitive 'to'
        if (parts.length > 1) {
            // Date Range.
            const mStart = moment.utc(parts[0].trim());
            const mEnd = moment.utc(parts[1].trim());

            startDate = new Date(mStart.year(), mStart.month(), mStart.date(), mStart.hour(), mStart.minute());
            startTimeString = moment(startDate).format('HH:mm');
            if ((this.timeDurationUnit === TimeDurationUnit.Seconds)) {
                startDate = new Date(mStart.year(), mStart.month(), mStart.date(), mStart.hour(), mStart.minute(), mStart.second());
                startTimeString = moment(startDate).format('HH:mm:ss');
            }

            endDate = new Date(mEnd.year(), mEnd.month(), mEnd.date(), mEnd.hour(), mEnd.minute());
            endTimeString = moment(endDate).format('HH:mm');
            if ((this.timeDurationUnit === TimeDurationUnit.Seconds)) {
                endDate = new Date(mEnd.year(), mEnd.month(), mEnd.date(), mEnd.hour(), mEnd.minute(), mEnd.second());
                endTimeString = moment(endDate).format('HH:mm:ss');
            }
        } else {
            // single date figure
            value = value.split(/exact/i)[0]; // case insensitive 'exact'
            startDate = moment(value).toDate();
            startTimeString = moment(startDate).format('HH:mm');
        }
        return {
            startDate: startDate,
            startTime: startTimeString,
            endDate: endDate,
            endTime: endTimeString
        };
    }

    private buildSearchParams(searchSpecification: SearchSpecification): URLSearchParams {
        const params = new URLSearchParams('', new DataMinerQueryEncoder());

        Object.keys(searchSpecification).map((key) => {
            if (searchSpecification.hasOwnProperty(key)) {
                if (typeof (searchSpecification[key]) === 'object') {
                    const childObj = searchSpecification[key];
                    if (childObj != null) {
                        if (childObj && childObj instanceof Array && (childObj.length > 0) && (typeof (childObj[0]) !== 'object')) {
                            // Arrays in the DM api should be added to the query string as
                            // PropertyName=value1,value2,value3
                            params.set(key, searchSpecification[key]);
                        } else {
                            Object.keys(childObj).map((childKey) => {
                                if (typeof (childObj[childKey]) === 'object') {
                                    // Object arrays in the DM api are for filters which are added as
                                    // field=1&field=2
                                    this.addSubObjectProperties(params, childObj[childKey]);
                                } else {
                                    params.set(childKey, childObj[childKey]);
                                }
                            });
                        }
                    }
                } else {
                    // standard property
                    params.set(key, searchSpecification[key]);
                }
            }
        });

        return params;
    }

    private addSubObjectProperties(params: URLSearchParams, object: Object) {
        Object.keys(object).map((key) => {
            if (object.hasOwnProperty(key)) {
                if (typeof (object[key]) === 'object') {
                    this.addSubObjectProperties(params, object[key]);
                } else {
                    params.set(key, object[key]);
                }
            }
        });
    }

    private getNumberFormat(column: FeedMetadataColumn): IColumnFormat {
        if (!column.displayFormat) {
            // default to number
            return new ColumnFormatNumber();
        }

        const formatParts = column.displayFormat.split(':', 3);
        if (formatParts.length === 1) {
            const columnFormat = new ColumnFormatNumber();
            columnFormat.digitInfo = column.displayFormat;

            return columnFormat;
        }

        const columnFormat = new ColumnFormatCurrency();
        columnFormat.digitInfo = formatParts[0] || columnFormat.digitInfo;
        columnFormat.symbolDisplay = !(formatParts[1] !== undefined && formatParts[1] === 'false');
        columnFormat.currencyCode = formatParts[2] || columnFormat.currencyCode;

        return columnFormat;

    }

    private getDateFormat(column: FeedMetadataColumn): ColumnFormatDate {
        const columnFormat = new ColumnFormatDate();
        if (column.dataType.toLowerCase() === 'date') {
            columnFormat.format = 'M/D/YYYY';
        } else {
            columnFormat.format = 'M/D/YYYY HH:mm';
        }

        if (column.displayFormat && this.dateTimeAliases[column.displayFormat.toLowerCase()]) {
            columnFormat.format = this.dateTimeAliases[column.displayFormat.toLowerCase()];
        }

        return columnFormat;
    }

    private getBoolFormat(column: FeedMetadataColumn): IColumnFormat {
        const columnFormat = new ColumnFormatBoolean();

        if (column.displayFormat) {
            columnFormat.format = column.displayFormat || 'icon';
        }

        return columnFormat;
    }

    private getColumnsForFilterType(columns: FeedMetadataColumn[], filterType: string): FeedMetadataColumn[] {
        const filterTypeColumns = [];
        columns.forEach(column => {
            if (column.enableFiltering &&
                column.filterType &&
                column.filterType.toLowerCase() === filterType.toLowerCase()) {
                filterTypeColumns.push(column);
            }
        });

        return filterTypeColumns;
    }

    private getColumnConfiguration(feedColumns: FeedMetadataColumn[], filterType: string): FeedSelectorConfiguration {
        let configuration: FeedSelectorConfiguration;
        if (filterType && feedColumns) {
            // Need the selector name, feed key, target identity and source columns at the column level

            // get config for this filter type.  TODO: this should come down in the columns metadata.
            configuration = this.feedSelectorConfigService.configuration
                .find(x => x.selectorType.toLowerCase() === filterType.toLowerCase());

            if (configuration) {
                // if there is config, find all columns with this filter type
                const columns = this.getColumnsForFilterType(feedColumns, configuration.selectorType);

                // need to parse the fieldNames to figure out the id and display fields and update the config.
                const idColumn = columns.find(x => x.fieldName.toLowerCase().endsWith('id'));
                const displayColumn = columns.find(x => x.fieldName.toLowerCase().endsWith('name'));

                if (idColumn) {
                    configuration.targetIdentityField = idColumn.fieldName;
                }
                if (displayColumn) {
                    configuration.targetDisplayField = displayColumn.fieldName;
                }

            }
        }

        return configuration;
    }
}



// WEBPACK FOOTER //
// ./src/app/main/feeds/shared/feed-manager.service.ts